import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { BookCharacter } from "../../../shared/characterRoster";
import {
  defaultBookNarration,
  ensureCharacterColors,
  getNarrationFromBookMetadata,
  resolveSpeakerColor,
  withNarrationInMetadata,
} from "../../../shared/bookNarration";
import {
  attributeDialogueInChapter,
  speakersInSpans,
  type DialogueSpan,
} from "../../../shared/dialogueAttribute";
import {
  loadNarratorPrefs,
  saveNarratorPrefs,
  NARRATOR_FONT_OPTIONS,
  type NarratorReadingPrefs,
} from "../../../shared/narratorModePrefs";
import { userFacingError } from "../../../shared/userFacingError";
import {
  SPEAKER_PALETTE_IDS,
  SPEAKER_PALETTE_LABELS,
  type SpeakerPaletteId,
} from "../../../shared/speakerPalette";
import {
  activeTakes,
  coveragePercent,
  generateTakeId,
  recomputeCoverage,
  selectTakeInTimeline,
  type ChapterEditMap,
  type NarrationTake,
} from "../../../shared/narrationTakes";
import { validateAcxMetrics, acxAllPassed } from "../../../shared/acxValidate";
import { buildAcxChapterMasterChecklist } from "../../../shared/audiobookExport";
import { listChapterParagraphs, type ChapterDocContent } from "../../../shared/chapterParagraphs";
import type { Chapter } from "../../types";
import { cloudAuthDesktopV2 } from "../../services/cloudAuthDesktopV2";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";

type MicLevel = { peakDb: number; clipping: boolean };

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function encodeWavFromFloat32(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const dataSize = samples.length * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeStr = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i));
  };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);
  let o = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]!));
    view.setInt16(o, s < 0 ? s * 32768 : s * 32767, true);
    o += 2;
  }
  return buffer;
}

function measurePeakDb(samples: Float32Array): number {
  let peak = 0;
  for (let i = 0; i < samples.length; i++) {
    const a = Math.abs(samples[i]!);
    if (a > peak) peak = a;
  }
  return peak <= 0 ? -100 : 20 * Math.log10(peak);
}

function measureRmsDb(samples: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i]! * samples[i]!;
  const rms = samples.length ? Math.sqrt(sum / samples.length) : 0;
  return rms <= 0 ? -100 : 20 * Math.log10(rms);
}

export function NarratorModePanel({
  bookId,
  bookMetadata,
  chapter,
  characters,
  chapters,
  onExit,
  onSelectChapter,
  onBookMetadataChange,
  onCharactersChange,
}: {
  bookId: string;
  bookMetadata: Record<string, unknown>;
  chapter: Chapter;
  characters: BookCharacter[];
  chapters: Array<{ id: string; title: string; order: number }>;
  onExit: () => void;
  onSelectChapter: (chapterId: string) => void;
  onBookMetadataChange: (metadata: Record<string, unknown>) => void;
  onCharactersChange: (characters: BookCharacter[]) => void;
}) {
  const [prefs, setPrefs] = useState<NarratorReadingPrefs>(() => loadNarratorPrefs());
  const [editMap, setEditMap] = useState<ChapterEditMap | null>(null);
  const [spans, setSpans] = useState<DialogueSpan[]>([]);
  const [recording, setRecording] = useState(false);
  const [micLevel, setMicLevel] = useState<MicLevel>({ peakDb: -60, clipping: false });
  const [status, setStatus] = useState("Ready");
  const [keyOpen, setKeyOpen] = useState(true);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [selectedTakeId, setSelectedTakeId] = useState<string | null>(null);
  const [acxReport, setAcxReport] = useState<ReturnType<typeof validateAcxMetrics> | null>(null);
  const [stitchSucceeded, setStitchSucceeded] = useState(false);
  const [m4bHintStatus, setM4bHintStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [playbackParagraphId, setPlaybackParagraphId] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const meterRafRef = useRef<number>(0);
  const recordStartParaRef = useRef<string>("");
  const streamRef = useRef<MediaStream | null>(null);
  const playbackRef = useRef<HTMLAudioElement | null>(null);

  const narration = useMemo(
    () => getNarrationFromBookMetadata(bookMetadata),
    [bookMetadata]
  );

  const paragraphIds = useMemo(() => {
    try {
      return listChapterParagraphs(chapter.content as unknown as ChapterDocContent).map((p) => p.id);
    } catch {
      return [];
    }
  }, [chapter.content]);

  const speakerList = useMemo(
    () => speakersInSpans(spans, characters),
    [spans, characters]
  );

  const colorMap = useMemo(() => {
    const map: Record<string, { highlight: string; text: string }> = {
      narrator: resolveSpeakerColor("narrator", characters, narration),
    };
    for (const c of characters) {
      map[c.id] = resolveSpeakerColor(c.id, characters, narration);
    }
    return map;
  }, [characters, narration]);

  const coveragePct = editMap ? coveragePercent(editMap.coverage) : 0;
  const takes = editMap ? activeTakes(editMap) : [];

  const persistPrefs = useCallback((next: NarratorReadingPrefs) => {
    const normalized = { ...next };
    setPrefs(normalized);
    saveNarratorPrefs(normalized);
  }, []);

  const refreshEditMap = useCallback(async () => {
    const api = window.desktopV2Api;
    if (!api?.narrationLoadEditMap) return;
    const map = await api.narrationLoadEditMap({ bookId, chapterId: chapter.id });
    setEditMap(map);
  }, [bookId, chapter.id]);

  useEffect(() => {
    void refreshEditMap();
  }, [refreshEditMap]);

  useEffect(() => {
    const next = attributeDialogueInChapter(chapter.content, characters);
    setSpans(next);
  }, [chapter.content, characters]);

  useEffect(() => {
    return () => {
      if (meterRafRef.current) cancelAnimationFrame(meterRafRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      void audioCtxRef.current?.close();
    };
  }, []);

  const startMeter = useCallback((stream: MediaStream) => {
    const ctx = new AudioContext();
    audioCtxRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    analyserRef.current = analyser;
    const data = new Float32Array(analyser.fftSize);
    const tick = () => {
      analyser.getFloatTimeDomainData(data);
      const peakDb = measurePeakDb(data);
      setMicLevel({ peakDb, clipping: peakDb > -1 });
      meterRafRef.current = requestAnimationFrame(tick);
    };
    meterRafRef.current = requestAnimationFrame(tick);
  }, []);

  const ensureMic = useCallback(async () => {
    if (streamRef.current) return streamRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });
    streamRef.current = stream;
    startMeter(stream);
    return stream;
  }, [startMeter]);

  const stopRecording = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      setRecording(false);
      return;
    }
    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
      recorder.stop();
    });
    setRecording(false);
    mediaRecorderRef.current = null;

    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    chunksRef.current = [];
    if (blob.size < 100) {
      setStatus("Take too short — discarded");
      return;
    }

    // Decode to PCM via Web Audio, then encode WAV
    const arrayBuf = await blob.arrayBuffer();
    const ctx = new AudioContext({ sampleRate: 44100 });
    let audioBuffer: AudioBuffer;
    try {
      audioBuffer = await ctx.decodeAudioData(arrayBuf.slice(0));
    } catch {
      setStatus("Could not decode recording");
      await ctx.close();
      return;
    }
    const ch0 = audioBuffer.getChannelData(0);
    const mono = new Float32Array(ch0.length);
    mono.set(ch0);
    if (audioBuffer.numberOfChannels > 1) {
      const ch1 = audioBuffer.getChannelData(1);
      for (let i = 0; i < mono.length; i++) mono[i] = (mono[i]! + ch1[i]!) / 2;
    }
    // Resample if needed
    let samples = mono;
    let sampleRate = audioBuffer.sampleRate;
    if (sampleRate !== 44100) {
      const ratio = 44100 / sampleRate;
      const outLen = Math.floor(mono.length * ratio);
      const out = new Float32Array(outLen);
      for (let i = 0; i < outLen; i++) {
        const src = i / ratio;
        const i0 = Math.floor(src);
        const i1 = Math.min(mono.length - 1, i0 + 1);
        const t = src - i0;
        out[i] = mono[i0]! * (1 - t) + mono[i1]! * t;
      }
      samples = out;
      sampleRate = 44100;
    }
    await ctx.close();

    const peakDb = measurePeakDb(samples);
    const rmsDb = measureRmsDb(samples);
    const durationMs = Math.round((samples.length / sampleRate) * 1000);
    const wav = encodeWavFromFloat32(samples, sampleRate);
    const takeId = generateTakeId();
    const startParagraphId = recordStartParaRef.current || paragraphIds[0] || "p0";
    const endParagraphId =
      paragraphIds[Math.min(paragraphIds.length - 1, paragraphIds.indexOf(startParagraphId) + 3)] ||
      startParagraphId;

    const api = window.desktopV2Api;
    if (!api?.narrationSaveTake) {
      setStatus("Narration API unavailable");
      return;
    }

    const take: NarrationTake = {
      id: takeId,
      chapterId: chapter.id,
      filePath: "",
      createdAt: new Date().toISOString(),
      startParagraphId,
      endParagraphId,
      trimInMs: 0,
      trimOutMs: durationMs,
      peakDb,
      rmsDb,
      durationMs,
      status: "candidate",
      label: `Take ${(editMap?.takes.length ?? 0) + 1}`,
    };

    const result = await api.narrationSaveTake({
      bookId,
      chapterId: chapter.id,
      take,
      wavBase64: arrayBufferToBase64(wav),
      paragraphIds,
      autoSelect: true,
    });
    setEditMap(result.editMap);
    setSelectedTakeId(takeId);
    setStatus(`Saved ${take.label} (${(durationMs / 1000).toFixed(1)}s)`);
  }, [bookId, chapter.id, editMap?.takes.length, paragraphIds]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await ensureMic();
      if (micLevel.clipping) {
        setStatus("Input clipping — lower gain before recording");
      }
      chunksRef.current = [];
      recordStartParaRef.current = paragraphIds[0] || "";
      // Prefer paragraph under selection in future; default first uncovered
      if (editMap) {
        const uncovered = paragraphIds.find((id) => editMap.coverage[id] !== "complete");
        if (uncovered) recordStartParaRef.current = uncovered;
      }
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setRecording(true);
      setStatus("Recording…");
    } catch (err) {
      setStatus(userFacingError(err, "generic"));
    }
  }, [ensureMic, micLevel.clipping, paragraphIds, editMap]);

  const punchAndRoll = useCallback(async () => {
    if (recording) {
      await stopRecording();
    }
    setStatus(`Punch-back ${prefs.punchBackSec}s — re-record over last segment`);
    // Select last timeline take and trim its end by punchBack
    if (editMap && editMap.timeline.length > 0) {
      const last = editMap.timeline[editMap.timeline.length - 1]!;
      const take = editMap.takes.find((t) => t.id === last.takeId);
      if (take && window.desktopV2Api?.narrationUpdateEditMap) {
        const punchMs = prefs.punchBackSec * 1000;
        const newOut = Math.max(take.trimInMs + 100, (take.trimOutMs ?? take.durationMs) - punchMs);
        const takes = editMap.takes.map((t) =>
          t.id === take.id ? { ...t, trimOutMs: newOut, status: "selected" as const } : t
        );
        const timeline = editMap.timeline.map((seg) =>
          seg.takeId === take.id ? { ...seg, trimOutMs: newOut } : seg
        );
        const coverage = recomputeCoverage({ ...editMap, takes, timeline }, paragraphIds);
        const next = await window.desktopV2Api.narrationUpdateEditMap({
          ...editMap,
          takes,
          timeline,
          coverage,
        });
        setEditMap(next);
        recordStartParaRef.current = take.startParagraphId;
      }
    }
    await startRecording();
  }, [recording, stopRecording, prefs.punchBackSec, editMap, paragraphIds, startRecording]);

  const playTake = useCallback(async (take: NarrationTake) => {
    const api = window.desktopV2Api;
    if (!api?.narrationReadTakeBase64) return;
    const b64 = await api.narrationReadTakeBase64({ filePath: take.filePath });
    if (!b64) {
      setStatus("Take file missing");
      return;
    }
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: "audio/wav" });
    const url = URL.createObjectURL(blob);
    playbackRef.current?.pause();
    const audio = new Audio(url);
    playbackRef.current = audio;
    audio.onended = () => {
      URL.revokeObjectURL(url);
      setPlaybackParagraphId(null);
    };
    setPlaybackParagraphId(take.startParagraphId);
    setStatus(`Playing ${take.label ?? take.id}`);
    await audio.play();
  }, []);

  const selectTake = useCallback(
    async (takeId: string) => {
      if (!editMap || !window.desktopV2Api?.narrationUpdateEditMap) return;
      const next = selectTakeInTimeline(editMap, takeId);
      next.coverage = recomputeCoverage(next, paragraphIds);
      const saved = await window.desktopV2Api.narrationUpdateEditMap(next);
      setEditMap(saved);
      setSelectedTakeId(takeId);
      setStatus("Take selected for chapter timeline");
    },
    [editMap, paragraphIds]
  );

  const deleteTake = useCallback(
    async (takeId: string) => {
      if (!editMap || !window.desktopV2Api?.narrationDeleteTake) return;
      const selected = editMap.timeline.some((s) => s.takeId === takeId);
      if (selected && !confirm("Delete the selected take for this span?")) return;
      const saved = await window.desktopV2Api.narrationDeleteTake({
        bookId,
        chapterId: chapter.id,
        takeId,
        paragraphIds,
      });
      setEditMap(saved);
      if (selectedTakeId === takeId) setSelectedTakeId(null);
      setStatus("Take deleted");
    },
    [editMap, bookId, chapter.id, paragraphIds, selectedTakeId]
  );

  const updateTakeTrim = useCallback(
    async (takeId: string, trimInMs: number, trimOutMs: number) => {
      if (!editMap || !window.desktopV2Api?.narrationUpdateEditMap) return;
      const takes = editMap.takes.map((t) =>
        t.id === takeId
          ? {
              ...t,
              trimInMs: Math.max(0, trimInMs),
              trimOutMs: Math.min(t.durationMs, Math.max(trimInMs + 50, trimOutMs)),
            }
          : t
      );
      const timeline = editMap.timeline.map((seg) => {
        if (seg.takeId !== takeId) return seg;
        const t = takes.find((x) => x.id === takeId)!;
        return {
          takeId,
          trimInMs: t.trimInMs,
          trimOutMs: t.trimOutMs ?? t.durationMs,
        };
      });
      const coverage = recomputeCoverage({ ...editMap, takes, timeline }, paragraphIds);
      const saved = await window.desktopV2Api.narrationUpdateEditMap({
        ...editMap,
        takes,
        timeline,
        coverage,
      });
      setEditMap(saved);
    },
    [editMap, paragraphIds]
  );

  const captureRoomTone = useCallback(async () => {
    try {
      const stream = await ensureMic();
      setStatus("Capturing room tone (3s)…");
      const chunks: Blob[] = [];
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      recorder.start();
      await new Promise((r) => setTimeout(r, 3000));
      await new Promise<void>((resolve) => {
        recorder.onstop = () => resolve();
        recorder.stop();
      });
      const blob = new Blob(chunks, { type: "audio/webm" });
      const ctx = new AudioContext({ sampleRate: 44100 });
      const buf = await ctx.decodeAudioData((await blob.arrayBuffer()).slice(0));
      const mono = new Float32Array(buf.length);
      mono.set(buf.getChannelData(0));
      await ctx.close();
      const wav = encodeWavFromFloat32(mono, 44100);
      const api = window.desktopV2Api;
      if (!api?.narrationSaveRoomTone) return;
      const saved = await api.narrationSaveRoomTone({
        bookId,
        chapterId: chapter.id,
        wavBase64: arrayBufferToBase64(wav),
      });
      setEditMap(saved);
      setStatus("Room tone saved");
    } catch (err) {
      setStatus(userFacingError(err, "generic"));
    }
  }, [ensureMic, bookId, chapter.id]);

  const stitchAndValidate = useCallback(async () => {
    if (!window.desktopV2Api?.narrationStitch) return;
    setBusy(true);
    setStatus("Stitching takes…");
    try {
      const result = await window.desktopV2Api.narrationStitch({
        bookId,
        chapterId: chapter.id,
        targetRmsDb: prefs.targetRmsDb,
        paragraphIds,
      });
      setEditMap(result.editMap);
      const checks = validateAcxMetrics(result.metrics);
      setAcxReport(checks);
      setStitchSucceeded(true);
      setStatus(
        acxAllPassed(checks)
          ? "Master ready — ACX checks passed"
          : "Master ready — some ACX checks need attention"
      );
    } catch (err) {
      setStatus(userFacingError(err, "generic"));
    } finally {
      setBusy(false);
    }
  }, [bookId, chapter.id, prefs.targetRmsDb, paragraphIds]);

  const publishAudio = useCallback(async () => {
    if (!window.desktopV2Api?.narrationPublishChapter) return;
    setBusy(true);
    setStatus("Uploading chapter narration…");
    try {
      const token = await cloudAuthDesktopV2.getValidAccessToken().catch(() => null);
      if (!token) {
        setStatus("Sign in to publish narration");
        return;
      }
      const result = await window.desktopV2Api.narrationPublishChapter({
        bookId,
        chapterId: chapter.id,
        narratorName: narration.narratorName || "Narrator",
        authToken: token,
      });
      setStatus(result.ok ? "Published to cloud — collaborators can listen" : result.error || "Upload failed");
    } catch (err) {
      setStatus(userFacingError(err, "generic"));
    } finally {
      setBusy(false);
    }
  }, [bookId, chapter.id, narration.narratorName]);

  const exportM4bHint = useCallback(async () => {
    const checklist = buildAcxChapterMasterChecklist(
      chapter.title || "Chapter",
      Boolean(stitchSucceeded),
      Boolean(acxReport && acxAllPassed(acxReport))
    ).join("\n");
    const api = window.desktopV2Api;
    try {
      if (api?.narrationExportM4bHint) {
        const result = await api.narrationExportM4bHint();
        const hint = result.message?.trim() || checklist;
        await navigator.clipboard.writeText(hint);
        setM4bHintStatus("Copied ffmpeg / m4b export hint to clipboard");
        setStatus("m4b hint copied");
        return;
      }
      await navigator.clipboard.writeText(
        [
          "# Export for m4b — stitch chapter masters, then concat with ffmpeg:",
          "# ffmpeg -f concat -safe 0 -i chapters.txt -c copy book.m4b",
          "",
          checklist,
        ].join("\n")
      );
      setM4bHintStatus("Copied ACX checklist + ffmpeg concat hint");
      setStatus("m4b checklist copied");
    } catch (err) {
      setM4bHintStatus(userFacingError(err, "generic"));
    }
  }, [acxReport, chapter.title, stitchSucceeded]);

  const uploadExternal = useCallback(async () => {
    if (!window.desktopV2Api?.narrationUploadExternalFile) return;
    setBusy(true);
    try {
      const token = await cloudAuthDesktopV2.getValidAccessToken().catch(() => null);
      if (!token) {
        setStatus("Sign in to upload");
        return;
      }
      const result = await window.desktopV2Api.narrationUploadExternalFile({
        bookId,
        chapterId: chapter.id,
        narratorName: narration.narratorName || "Narrator",
        authToken: token,
      });
      if (result.canceled) {
        setStatus("Upload canceled");
      } else if (result.ok) {
        setStatus("External recording uploaded");
      } else {
        setStatus(result.error || "Upload failed");
      }
    } finally {
      setBusy(false);
    }
  }, [bookId, chapter.id, narration.narratorName]);

  const setPalette = useCallback(
    (paletteId: SpeakerPaletteId) => {
      persistPrefs({ ...prefs, paletteId });
      const nextNarration = {
        ...narration,
        paletteId,
        narratorColor:
          paletteId === narration.paletteId
            ? narration.narratorColor
            : defaultBookNarration(paletteId).narratorColor,
      };
      onBookMetadataChange(withNarrationInMetadata(bookMetadata, nextNarration));
      const recolored = ensureCharacterColors(
        characters.map((c) => ({ ...c, color: undefined })),
        paletteId
      );
      onCharactersChange(recolored);
    },
    [prefs, persistPrefs, narration, bookMetadata, onBookMetadataChange, characters, onCharactersChange]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (target?.closest?.('[data-slot="select-trigger"], [data-slot="select-content"]')) return;
      if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        if (recording) void stopRecording();
        else void startRecording();
      } else if (e.key === "[") {
        e.preventDefault();
        void punchAndRoll();
      } else if (e.key === "Escape") {
        onExit();
      } else if (e.key === " " && !recording) {
        // don't steal space from buttons
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [recording, startRecording, stopRecording, punchAndRoll, onExit]);

  const chapterBody = useMemo(() => {
    try {
      const paras = listChapterParagraphs(chapter.content as unknown as ChapterDocContent);
      return paras.map((p) => {
        const coverage = editMap?.coverage[p.id] ?? "none";
        const isPlay = playbackParagraphId === p.id;
        // Apply inline speaker highlights via spans
        const paraSpans = spans.filter((s) => s.paragraphId === p.id);
        let nodes: ReactNode = p.text;
        if (prefs.highlightsEnabled && paraSpans.length > 0) {
          const parts: ReactNode[] = [];
          let cursor = 0;
          const sorted = [...paraSpans].sort((a, b) => a.from - b.from);
          sorted.forEach((s, i) => {
            if (s.from > cursor) {
              parts.push(<span key={`t-${i}-pre`}>{p.text.slice(cursor, s.from)}</span>);
            }
            const color = colorMap[s.speakerId] ?? colorMap.narrator!;
            parts.push(
              <mark
                key={`s-${i}`}
                className="speaker-highlight"
                style={{ backgroundColor: color.highlight, color: color.text }}
                data-speaker-id={s.speakerId}
              >
                {p.text.slice(s.from, s.to)}
              </mark>
            );
            cursor = s.to;
          });
          if (cursor < p.text.length) {
            parts.push(<span key="t-tail">{p.text.slice(cursor)}</span>);
          }
          nodes = parts;
        }
        return (
          <p
            key={p.id}
            data-paragraph-id={p.id}
            className={cn(
              "relative mb-[1em] pl-3.5",
              isPlay && "rounded-sm ring-2 ring-primary/45 ring-offset-4 ring-offset-background"
            )}
          >
            <span
              className={cn(
                "absolute inset-y-[0.15em] left-0 w-1 rounded-sm bg-transparent",
                coverage === "complete" && "bg-emerald-500",
                coverage === "partial" && "bg-amber-500"
              )}
              title={coverage}
              aria-hidden
            />
            {nodes}
          </p>
        );
      });
    } catch {
      return <p className="relative mb-[1em] pl-3.5">Unable to render chapter.</p>;
    }
  }, [
    chapter.content,
    editMap?.coverage,
    spans,
    prefs.highlightsEnabled,
    colorMap,
    playbackParagraphId,
  ]);

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-background text-foreground"
      role="application"
      aria-label="Narrator Mode"
    >
      <header className="flex shrink-0 items-center justify-between gap-2.5 border-b border-border bg-card/80 px-3.5 py-2">
        <div className="flex items-center gap-2">
          <Select value={chapter.id} onValueChange={(value) => onSelectChapter(value)}>
            <SelectTrigger className="h-7.5 max-w-64 text-[0.8125rem]" aria-label="Chapter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...chapters]
                .sort((a, b) => a.order - b.order)
                .map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.title || "Untitled"}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Badge variant="secondary" title="Recording coverage">
            {coveragePct}% recorded
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => setPrefsOpen((v) => !v)}>
            Reading prefs
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onExit}>
            Exit Narrator
          </Button>
        </div>
      </header>

      {prefsOpen ? (
        <div
          className="flex flex-wrap items-center gap-3 border-b border-border bg-muted/35 px-3.5 py-1.5 text-[0.78rem]"
          role="region"
          aria-label="Reading preferences"
        >
          <label className="inline-flex items-center gap-1.5">
            Font
            <Select
              value={NARRATOR_FONT_OPTIONS.find((f) => f.css === prefs.fontFamily)?.id ?? "palatino"}
              onValueChange={(value) => {
                const opt = NARRATOR_FONT_OPTIONS.find((f) => f.id === value);
                if (opt) persistPrefs({ ...prefs, fontFamily: opt.css });
              }}
            >
              <SelectTrigger size="sm" aria-label="Font">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NARRATOR_FONT_OPTIONS.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="inline-flex items-center gap-1.5">
            Size
            <Input
              type="range"
              className="h-4 w-32 cursor-pointer appearance-auto border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
              min={16}
              max={42}
              value={prefs.fontSizePx}
              onChange={(e) => persistPrefs({ ...prefs, fontSizePx: Number(e.target.value) })}
            />
            <span>{prefs.fontSizePx}px</span>
          </label>
          <label className="inline-flex items-center gap-1.5">
            Line height
            <Input
              type="range"
              className="h-4 w-32 cursor-pointer appearance-auto border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
              min={13}
              max={24}
              value={Math.round(prefs.lineHeight * 10)}
              onChange={(e) =>
                persistPrefs({ ...prefs, lineHeight: Number(e.target.value) / 10 })
              }
            />
          </label>
          <label className="inline-flex items-center gap-1.5">
            Palette
            <Select value={prefs.paletteId} onValueChange={(value) => setPalette(value as SpeakerPaletteId)}>
              <SelectTrigger size="sm" aria-label="Palette">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPEAKER_PALETTE_IDS.map((id) => (
                  <SelectItem key={id} value={id}>
                    {SPEAKER_PALETTE_LABELS[id]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="inline-flex items-center gap-1.5">
            <Checkbox
              checked={prefs.highlightsEnabled}
              onCheckedChange={(checked) =>
                persistPrefs({ ...prefs, highlightsEnabled: checked === true })
              }
            />
            Character highlights
          </label>
          <label className="inline-flex items-center gap-1.5">
            Punch-back
            <Input
              type="range"
              className="h-4 w-32 cursor-pointer appearance-auto border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
              min={5}
              max={50}
              value={Math.round(prefs.punchBackSec * 10)}
              onChange={(e) =>
                persistPrefs({ ...prefs, punchBackSec: Number(e.target.value) / 10 })
              }
            />
            <span>{prefs.punchBackSec.toFixed(1)}s</span>
          </label>
        </div>
      ) : null}

      <div className="relative flex min-h-0 flex-1">
        <div
          className="mx-auto flex-1 overflow-auto py-8 pr-6 pl-10"
          style={{
            fontFamily: prefs.fontFamily,
            fontSize: `${prefs.fontSizePx}px`,
            lineHeight: prefs.lineHeight,
            maxWidth: `${prefs.maxMeasureCh}ch`,
          }}
        >
          <h1 className="mb-[1.25em] text-[1.35em] font-semibold">{chapter.title}</h1>
          {chapterBody}
        </div>

        <aside
          className={cn(
            "shrink-0 overflow-auto border-l border-border bg-muted/25 p-3 transition-[width]",
            keyOpen ? "w-46" : "w-11"
          )}
          aria-label="Speaker color key"
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full justify-start overflow-hidden px-1"
            onClick={() => setKeyOpen((v) => !v)}
            aria-expanded={keyOpen}
          >
            {keyOpen ? "Speakers ▾" : "Speakers ▸"}
          </Button>
          {keyOpen ? (
            <ul className="mt-3 flex list-none flex-col gap-2 p-0">
              {speakerList.map((s) => {
                const color = colorMap[s.id] ?? colorMap.narrator!;
                return (
                  <li className="flex items-center gap-2 text-sm" key={s.id}>
                    <span
                      className="inline-block size-3.5 shrink-0 rounded-[3px] border border-foreground/20 align-middle"
                      style={{ backgroundColor: color.highlight, color: color.text }}
                      aria-hidden
                    />
                    <span>{s.name}</span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </aside>
      </div>

      <div
        className="max-h-36 shrink-0 overflow-auto border-t border-border bg-muted/20 px-3 py-2"
        role="region"
        aria-label="Takes"
      >
        <div className="flex items-start gap-2.5">
          {takes.length === 0 ? (
            <span className="p-2 text-sm text-muted-foreground">
              No takes yet — press R to record
            </span>
          ) : (
            takes.map((take) => {
              const selected = editMap?.timeline.some((s) => s.takeId === take.id);
              return (
                <Card
                  key={take.id}
                  size="sm"
                  className={cn(
                    "min-w-40 max-w-56 gap-1.5 rounded-md bg-background px-2 py-1.5",
                    selected && "ring-primary/55",
                    selectedTakeId === take.id && "ring-2 ring-primary/30"
                  )}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    className="h-auto w-full justify-start overflow-hidden px-0 py-0 text-left text-xs font-semibold whitespace-normal hover:bg-transparent"
                    onClick={() => setSelectedTakeId(take.id)}
                  >
                    {take.label ?? take.id} · {(take.durationMs / 1000).toFixed(1)}s
                    {selected ? " ●" : ""}
                  </Button>
                  <div className="mt-1 flex gap-1">
                    <Button type="button" variant="ghost" size="xs" onClick={() => void playTake(take)}>
                      Play
                    </Button>
                    <Button type="button" variant="ghost" size="xs" onClick={() => void selectTake(take.id)}>
                      Use
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      className="text-destructive hover:text-destructive"
                      onClick={() => void deleteTake(take.id)}
                    >
                      Delete
                    </Button>
                  </div>
                  {selectedTakeId === take.id ? (
                    <div className="mt-1.5 flex flex-col gap-1 text-xs">
                      <label className="flex items-center gap-1">
                        In
                        <Input
                          type="range"
                          className="h-4 cursor-pointer appearance-auto border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
                          min={0}
                          max={take.durationMs}
                          value={take.trimInMs}
                          onChange={(e) =>
                            void updateTakeTrim(
                              take.id,
                              Number(e.target.value),
                              take.trimOutMs ?? take.durationMs
                            )
                          }
                        />
                      </label>
                      <label className="flex items-center gap-1">
                        Out
                        <Input
                          type="range"
                          className="h-4 cursor-pointer appearance-auto border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
                          min={0}
                          max={take.durationMs}
                          value={take.trimOutMs ?? take.durationMs}
                          onChange={(e) =>
                            void updateTakeTrim(take.id, take.trimInMs, Number(e.target.value))
                          }
                        />
                      </label>
                    </div>
                  ) : null}
                </Card>
              );
            })
          )}
        </div>
      </div>

      <footer className="flex shrink-0 flex-wrap items-center gap-3 border-t border-border px-4 py-2.5">
        <div
          className="flex min-w-28 items-center gap-1.5 text-xs"
          title={`Peak ${micLevel.peakDb.toFixed(1)} dB`}
        >
          <div
            className={cn(
              "h-2 min-w-0.5 max-w-20 rounded-sm",
              micLevel.clipping ? "bg-destructive" : "bg-emerald-500"
            )}
            style={{
              width: `${Math.min(100, Math.max(0, ((micLevel.peakDb + 60) / 60) * 100))}%`,
            }}
          />
          <span>{micLevel.peakDb.toFixed(0)} dB</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button type="button" variant="ghost" size="sm" onClick={() => void captureRoomTone()}>
            Room tone
          </Button>
          <Button
            type="button"
            variant={recording ? "destructive" : "outline"}
            className="min-w-22 rounded-full"
            onClick={() => void (recording ? stopRecording() : startRecording())}
          >
            {recording ? "Stop" : "Record"}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => void punchAndRoll()}>
            Punch
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy || !editMap?.timeline.length}
            onClick={() => void stitchAndValidate()}
          >
            Stitch
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => void publishAudio()}
          >
            Publish
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => void uploadExternal()}
          >
            Upload file…
          </Button>
        </div>
        <div className="ml-auto text-[0.8rem] text-muted-foreground" role="status">
          {status}
        </div>
      </footer>

      {acxReport ? (
        <div
          className="max-h-28 overflow-auto border-t border-border px-4 py-2 text-[0.8rem]"
          role="region"
          aria-label="ACX validation"
        >
          <strong>ACX checks</strong>
          <ul className="mt-1.5 list-disc pl-4.5">
            {acxReport.map((c) => (
              <li className={cn(c.ok ? "text-emerald-700 dark:text-emerald-400" : "text-destructive")} key={c.id}>
                {c.ok ? "✓" : "✗"} {c.label}: {c.detail}
              </li>
            ))}
          </ul>
          {stitchSucceeded && acxAllPassed(acxReport) ? (
            <div className="mt-2.5">
              <p className="mb-2 text-xs">
                Chapter master ready for ACX upload
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => void publishAudio()}
                >
                  Publish chapter audio
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => void exportM4bHint()}
                  title="Copy ffmpeg concat guidance for book.m4b"
                >
                  Export for m4b / Copy ffmpeg concat hint
                </Button>
              </div>
              <ul className="mt-2" aria-label="ACX chapter master checklist">
                {buildAcxChapterMasterChecklist(
                  chapter.title || "Chapter",
                  true,
                  true
                ).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              {m4bHintStatus ? (
                <p className="mt-2 text-xs" role="status">
                  {m4bHintStatus}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
